import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BookingStoreService } from '@modules/booking/store/booking.store.service';
import { UserProfile } from '@modules/identity/models/user';
import { IdentityStoreService } from '@modules/identity/store/services/identity-store.service';
import { Observable, Subject } from 'rxjs';
import { ClassroomCourse, Schedule } from '../../models';
import { ClassroomCourseApiService } from '../../store/services/classroom-course.api.service';
import { ClassroomCourseStoreService } from '../../store/services/classroom-course.store.service';
import { PhoneNumberValidators } from "@shared/validators/phone-number.validator";
import { CountryStoreService } from "@shared/store/country-store/services/country.store.service";
import { Country } from "@shared/models/country";
import { extractPhoneNumber } from "@core/utils/extract-phone-number";
import { AllowedCountryList } from "@shared/constants/allowed-countries";
import { filter, first, take, takeUntil } from "rxjs/operators";
import { isEmpty, isNull, isUndefined } from "lodash";
import { CustomerExperienceApiService } from "@shared/services/customer-experience.api.service";
import { NotificationService } from '@modules/core/services/notification.service';
import { classroomFeatureKey } from '../../store/classroom-course.state';

interface QueryParams {
  id?: string;
  date?: string;
  schedule?: number;
  participant?: number;
}

@Component({
  selector: 'app-classroom-participants',
  templateUrl: './classroom-participants.component.html',
  styleUrls: ['./classroom-participants.component.scss'],
})
export class ClassroomParticipantsComponent implements OnInit, OnDestroy {
  params: QueryParams;
  classroom$: Observable<ClassroomCourse>;
  countryList$: Observable<Country[]>;
  userProfile: UserProfile;
  participantForm: FormGroup;

  emailSuggestion: string = null;

  selfParticipant = new FormControl(false);

  private destroy$: Subject<any>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private fb: FormBuilder,
    private classroomStore: ClassroomCourseStoreService,
    private classroomApi: ClassroomCourseApiService,
    private countryStoreService: CountryStoreService,
    private identityStore: IdentityStoreService,
    private bookingStore: BookingStoreService,
    private customerExperienceApiService: CustomerExperienceApiService,
    private notificationService: NotificationService

  ) {
    this.destroy$ = new Subject();
    this.params = route.snapshot.queryParams;
    this.initParticipantForm();
  }

  get participants() {
    return this.participantForm.controls.participants as FormArray;
  }

  ngOnInit(): void {
    this.countryList$ = this.countryStoreService.getCountriesByIds(AllowedCountryList);
    this.classroom$ = this.classroomStore.getClassroomCourse(this.params.id);
    this.identityStore.getUserProfile().subscribe((profile) => {
      this.userProfile = profile;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  backPrevious(): void {
    this.location.back();
  }

  getSchedule(classroom: ClassroomCourse): Schedule {
    return classroom.schedules.find((schedule) => schedule.id == this.params.schedule);
  }

  convertToGroup(participant) {
    return participant as FormGroup;
  }

  checkSelfParticipant(participantGroup: FormGroup): void {
    const value = this.selfParticipant.value;

    if (value) {
      if (this.userProfile) {
        const { sub, name, email, phone_number } = this.userProfile;
        const [phonePrefix,phone] = extractPhoneNumber(phone_number);
        participantGroup.patchValue({
          sub: sub,
          name: name,
          email: email,
          phonePrefix,
          phone
        });
        participantGroup.disable();
        if(!phone_number) {
          participantGroup.get('phone').enable();
        }
      }
    } else {
      participantGroup.patchValue({
        sub: '',
        name: '',
        email: '',
        phonePrefix: '+47',
        phone: '',
      });
      participantGroup.enable();
    }
  }


  checkParticipantIdentity(participantGroup): void {
    const { phonePrefix,phone } = participantGroup.value;

    if(!phone) return;

    this.classroomApi.checkPartnerIdentity(phonePrefix+phone).pipe(
        first(),
        takeUntil(this.destroy$)
      )
      .subscribe(participant => {
      if(participant) {
        participantGroup.patchValue({ name: participant.name, email: participant.email });
      } else {
        participantGroup.patchValue({ name: '', email: '' });
      }
    },
    (error) => {
      this.notificationService.error(error, classroomFeatureKey);
    })
  }

  saveParticipants(): void {
    const  participants  = this.participantForm.getRawValue().participants.map(({phonePrefix, isInvalidEmail, ...participant}) => {
      return {...participant,phone: phonePrefix+participant.phone};
    });

    this.bookingStore.addParticipants(participants);

    const queryParams = {
      scheduleId: this.params.schedule,
      participant: this.params.participant,
    };
    this.router.navigate([`/booking/${this.params.id}/classroom/request/${this.params.date}`], {
      queryParams,
      skipLocationChange: true,
    });
  }

  private initParticipantForm(): void {
    this.participantForm = this.fb.group({ participants: this.fb.array([]) });

    for (let participant = 0; participant < this.params.participant; participant++) {
      this.participants.push(
        this.fb.group({
          sub: [''],
          name: ['', [Validators.required]],
          email: ['', [Validators.required, Validators.email]],
          isInvalidEmail: [false],
          phonePrefix:['+47',Validators.required],
          phone: ['', [
            Validators.required
          ]],
        },{
          validators: PhoneNumberValidators.validatePhoneNumberCountry
        })
      );
    }
  }

  validateEmail(participantGroup: FormGroup) {
    const { email } = participantGroup.value;

    if (participantGroup.controls?.email?.status === 'VALID') {
      this.customerExperienceApiService
        .getEmailValidation(email, 'multiple participant form')
        .pipe(
          filter(response => !isEmpty(response) && !isNull(response) && !isUndefined(response)),
          take(1)
        ).subscribe(
        (response) => {
            participantGroup.get('isInvalidEmail').patchValue(!response.isValid);
            this.emailSuggestion = response.suggestion;
        });
    }
  }
}
